import React, {useEffect, useState} from "react"
import PropTypes from "prop-types"
import {
    Box,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material"
import Plot from "react-plotly.js"
import {v4 as uuidv4} from "uuid"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import Plotly from 'plotly.js-basic-dist'
import {DATE_FORMAT_SHOW} from "../../../utils/constants"
import theme from "../../theme/theme"
import ptBR from "plotly.js-locales/pt-br"
import {getContrastSafeRandomColor} from "src/v2/utils/chart-colors"

Plotly.register(ptBR)
dayjs.extend(customParseFormat)

const ToggleButtonGroupSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0,
    '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        borderRadius: 0,
        borderColor: 'divider',
        borderWidth: 1,
        borderStyle: 'solid',
        '&:not(:first-of-type)': {borderLeft: 0},
    }
}

const ToggleButtonSx = {}

const TableSx = {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    backgroundImage: "none",
    paddingTop: 0.5,
    paddingBottom: 0.5,
    paddingRight: 1,
    "& .MuiTableCell-root": {
        borderBottom: "none",
    },
}

const MultiAxisChartComponent = (props) => {
    const [xAxis, setXAxis] = useState({name: "", values: []})
    const [dataset, setDataset] = useState([])
    const [removedDataset, setRemovedDataSet] = useState([])
    const [legend, setLegend] = useState([])
    const [itemsId, setItemsId] = useState([])
    const [plotKey, setPlotKey] = useState(Math.random() * 10)

    useEffect(() => {
        generateData()
    }, [props])

    const toNum = (v) => {
        if (v === null || v === undefined || v === 'null') return null
        const str = String(v).trim()
        if (str === '') return ''
        const maybeNum = Number(str.replace(',', '.'))
        return isNaN(maybeNum) ? str : maybeNum
    }

    const parseBRDate = (s) => dayjs(s, DATE_FORMAT_SHOW, true).toDate()

    const generateData = () => {
        setLegend([])
        const xRaw = props.xValue || []
        const xDates = xRaw.map(parseBRDate)

        setXAxis({
            name: props.xTitle || "",
            values: xDates,
            raw: xRaw
        })

        const newData = []
        const newItemsId = []
        const generatedColors = []

        ;(props.values || []).forEach((group) => {
            const newLegend = []

            ;(group.items || []).forEach((item) => {
                const useRightAxis = item.id === "second-axis"
                const yAxisKey = useRightAxis ? "y2" : "y"
                const color = props.randomColor
                    ? getContrastSafeRandomColor(theme.palette.background.chart, generatedColors, `${group.grupo}-${item.item}`)
                    : item.color
                if (props.randomColor) generatedColors.push(color)
                const id = uuidv4()

                const tooltipExtra = item.tooltipExtra || null

                newData.push({
                    id,
                    label: `${group.grupo} ${item.item}`,
                    data: (item.values || []).map(toNum),
                    color,
                    smoothing: props.tension || 0,
                    yAxisKey,
                    tooltipExtra
                })

                newLegend.push({id, color, item: item.item, variant: "outlined"})
                newItemsId.push(id)
            })

            setLegend((prev) => [...prev, {group: group.grupo, items: newLegend}])
        })

        setItemsId(newItemsId)
        setDataset(newData)
    }

    const disableEnableLine = (itemId) => {
        const item = dataset.find((d) => d.id === itemId)
        if (item) {
            setDataset((prev) => prev.filter((d) => d.id !== itemId))
            setRemovedDataSet((prev) => [...prev, item])
            setItemsId((prev) => prev.filter((id) => id !== itemId))
        } else {
            const back = removedDataset.find((d) => d.id === itemId)
            if (!back) return
            setDataset((prev) => [...prev, back])
            setRemovedDataSet((prev) => prev.filter((d) => d.id !== itemId))
            setItemsId((prev) => [...prev, itemId])
        }
    }

    const formatNumberBR = (num, decimals = 4) => {
        if (num === null || num === undefined || num === '') return ' '
        return new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num)
    }

    const traces = dataset.map((ds) => {
        const hovertext = xAxis.values.map((x, i) => {
            const yVal = ds.data[i] !== undefined && ds.data[i] !== null
                ? formatNumberBR(ds.data[i], 4)
                : ' '


            // Extrai a última parte do rótulo da linha 
            const labelParts = ds.label.split(' ')
            let yLabelKey = labelParts[labelParts.length - 1]

            let yLabel

            if (yLabelKey === 'PU_MTM') {
                yLabel = 'PU_MTM'
            } else if (yLabelKey === 'PUPAR') {
                yLabel = 'PUPAR'
            } else if (yLabelKey === '%PUPAR') {
                yLabel = 'PUPAR%'
            } else {
                // Fallback para o valor padrão do props 
                yLabel = props.yTitle || "PU"
            }

            const groupName = ds.label.split(' ')[0]
            let text =
                `<b>${groupName}</b>` +
                `<br>${xAxis.name || "Data"}: ${dayjs(x).format("DD/MM/YYYY")}` +
                `<br>${yLabel}: ${yVal}` // Usando o yLabel dinâmico

            // Tooltip EXTRA EXACTO do dia
            if (ds.tooltipExtra && ds.tooltipExtra[i]) {
                const row = ds.tooltipExtra[i]

                Object.entries(row).forEach(([key, value]) => {
                    if (value === null || value === undefined || value === ' ') {
                        text += `<br>${key}: `
                        return
                    }

                    const maybeNum = Number(String(value).replace(',', '.'))
                    const fmt = isNaN(maybeNum)
                        ? value
                        : formatNumberBR(maybeNum, 4)

                    text += `<br>${key}: ${fmt}`
                })
            }

            return text
        })

        return {
            x: xAxis.values,
            y: ds.data,
            type: "scatter",
            mode: "lines+markers",
            name: ds.label,
            line: {
                color: ds.color,
                width: 2,
                shape: "linear",
                smoothing: Math.max(0, Math.min(1.3, ds.smoothing)),
            },
            marker: {size: 8, color: ds.color},
            cliponaxis: true,
            hoverinfo: "text",
            hovertext,
            yaxis: ds.yAxisKey,
            connectgaps: true,
        }
    })

    const fontAxis = {
        family: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
        size: 12
    }

    const hasXVals = (xAxis.values || []).length > 0
    const minX = hasXVals ? xAxis.values[0] : undefined
    const maxX = hasXVals ? xAxis.values[xAxis.values.length - 1] : undefined

    const layout = {
        locale: "pt-BR",
        separators: ",.",
        paper_bgcolor: theme.palette.background.chart,   // fundo geral
        plot_bgcolor: theme.palette.background.chart,   // fundo geral
        autosize: true,
        doubleclick: false,
        title: {text: props.title || "", font: {size: 16}},
        margin: {l: 60, r: 60, t: 40, b: 50},
        showlegend: false,
        dragmode: "zoom",
        hovermode: 'closest',
        font: {
            color: "#ffffff",   // muda a cor global dos textos
            size: 14
        },
        xaxis: {
            title: {
                text: xAxis.name || props.xTitle || "Data",
                standoff: 15,
                font: fontAxis,
                yref: "paper",
            },
            type: "date",
            rangebreaks: [{pattern: "day of week", bounds: [6, 1]}],
            autorange: false,
            range: hasXVals ? [minX, maxX] : undefined,
            tickformat: "%d/%m/%Y",
            hoverformat: "%d/%m/%Y",
            tickmode: "array",
            categoryarray: props.xValue || [],
            tickangle: 0,
            ticklabelposition: "outside",
            automargin: true,
            zeroline: false,
            linewidth: 1,
            ticklen: 6,
            tickwidth: 1,
            showline: true,
            linecolor: "rgba(255, 255, 255, 1)",
            gridcolor: "rgba(255,255,255,0.2)",
            ticks: "outside",
        },
        yaxis: {
            title: {text: props.yTitle || "", font: fontAxis, standoff: 15, automargin: true},
            tickformat: ",.0f",
            separatethousands: true,
            ticks: "outside",
            ticklen: 6,
            tickwidth: 1,
            automargin: true,
            showline: true,
            linewidth: 1,
            linecolor: "rgba(255, 255, 255, 1)",
            gridcolor: "rgba(255,255,255,0.2)",
            zeroline: false,
            showgrid: true,
        },
        yaxis2: {
            title: {text: props.yRightTitle || "", font: fontAxis, standoff: 15, automargin: true},
            overlaying: "y",
            side: "right",
            tickformat: ",.2f",
            ticks: "outside",
            ticklen: 6,
            tickwidth: 1,
            showline: true,
            linewidth: 1,
            linecolor: "rgba(255, 255, 255, 1)",
            gridcolor: "rgba(255,255,255,0.2)",
            zeroline: false,
            automargin: true,
            showgrid: false,
            autorange: true,
        }
    }

    const DEFAULT_PLOT_HEIGHT = 480
    const MIN_PLOT_HEIGHT = 320
    const MAX_PLOT_HEIGHT = 900

    const plotHeight = (() => {
        const h = Number(props.height)
        if (!Number.isFinite(h) || h <= 0) return DEFAULT_PLOT_HEIGHT
        const scaled = h * 6
        return Math.min(Math.max(scaled, MIN_PLOT_HEIGHT), MAX_PLOT_HEIGHT)
    })()

    const gridButtons = legend.map((row, index) => (
        <TableRow key={`table-${index}`} index={index}>
            <TableCell sx={TableSx}>{row.group}</TableCell>
            <TableCell sx={TableSx}>
                <ToggleButtonGroup
                    size="small"
                    aria-label={row.group}
                    sx={ToggleButtonGroupSx}
                >
                    {row.items.map((item, indexItem) => (
                        <ToggleButton
                            selected={itemsId.some((obj) => obj === item.id)}
                            key={`${item.id}-${indexItem}`}
                            value={item.id}
                            onClick={() => disableEnableLine(item.id)}
                            disableRipple
                            sx={ToggleButtonSx}
                        >
                            <Box component="span"
                                 sx={{marginRight: 1, bgcolor: item.color, width: 18, height: 4}}
                            />
                            <Typography variant="body2">{item.item}</Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </TableCell>
        </TableRow>
    ))

    return (
        <div id={props.id}>
            <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
                <Grid item sm={12} md={12} xl={12} xs={12}>
                    <Table sx={TableSx}>
                        <TableBody>{gridButtons}</TableBody>
                    </Table>
                </Grid>
            </Grid>

            <Plot
                key={plotKey}
                data={traces}
                layout={layout}
                useResizeHandler
                style={{width: "100%", height: plotHeight}}
                config={{
                    responsive: true,
                    displaylogo: false,
                    scrollZoom: true,
                    modeBarButtonsToRemove: [
                        "select2d", "lasso2d", "zoom2d",
                        "resetScale2d", "zoomIn2d", "zoomOut2d"
                    ],
                    displayModeBar: true,
                    modeBarButtonsToAdd: [
                        {
                            name: 'Reset Axis',
                            icon: Plotly.Icons.home,
                            click: () => setPlotKey(Math.random() * 10),
                        }
                    ]
                }}
            />
        </div>
    )
}

MultiAxisChartComponent.propTypes = {
    id: PropTypes.string,
    xTitle: PropTypes.string,
    xValue: PropTypes.array,
    yTitle: PropTypes.string,
    yRightTitle: PropTypes.string,
    title: PropTypes.string,
    values: PropTypes.array,
    reloadChart: PropTypes.func,
    height: PropTypes.number,
    randomColor: PropTypes.bool,
    tension: PropTypes.number,
}

export default MultiAxisChartComponent
